<script setup lang="ts">
  import { ref } from 'vue'
  import { vElementVisibility } from '@vueuse/components'

  import { useSanitizer } from '@/composables/useSanitizer'
  import type { ThinkText } from '@/types/ThinkText'

  import { KsIcon } from '@aschehoug/kloss'
  import Typography from '@/components/common/Typography.vue'

  const props = defineProps<{
    element: ThinkText
  }>()

  const { sanitize } = useSanitizer()

  const isTextInView = ref<boolean>(false)
  const sanitizedRichText = sanitize(props.element.richText)
</script>

<template>
  <section :class="['think-text-wrapper', props.element.size, element.colorPair]">
    <div :class="['think-text', props.element.size]">
      <div class="title-line">
        <KsIcon id="thought-bubble" />
        <Typography
          component="h2"
          variant="h3"
          class="title"
        >
          {{ element.title }}
        </Typography>
      </div>

      <span
        v-element-visibility="(isVisible) => isTextInView = isVisible"
        class="rich-text"
        :class="{ [props.element.size]: true, fade: isTextInView }"
        v-html="sanitizedRichText"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  .think-text-wrapper {
    background-color: var(--color-pair-background-color);
    color: var(--color-pair-color);

    &.large {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      width: 100%;
    }

    &.small {
      margin: 0 auto;
      max-width: $vgs-max-width-content;
    }
  }

  .think-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 742px;
    margin: 0 24px;

    &.large {
      gap: 24px;
      padding: 160px 0;
    }

    &.small {
      gap: 16px;
      padding: 40px;
      margin-bottom: 96px;
    }
  }

  .think-text-wrapper:last-child {
    .think-text.small {
      margin-bottom: 96px;
    }
  }

  .title-line {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .title {
    margin-bottom: 0;
  }

  .rich-text {
    $transition-text: opacity .3s ease-in;

    transition: $transition-text;

    &.large {
      opacity: 0;

      :deep(strong) {
        opacity: 0;
      }

      &:deep(.cms-p) {
        @include h1;

        line-height: 1.3;
        font-weight: 200;
        margin: 0;
      }
    }

    &.small {
      &:deep(.cms-p) {
        margin: 0;
      }
    }

    :deep(strong) {
      transition: $transition-text;
      transition-delay: .1s;
      font-weight: 800;

      @media (prefers-reduced-motion) {
        transition: none;
        opacity: 1;
      }
    }

    @include vgs-media-mobile {
      @include h2;
    }

    @media (prefers-reduced-motion) {
      transition: none;
      opacity: 1;
    }

    &.fade {
      opacity: 1;

      :deep(strong) {
        opacity: 1;
      }
    }
  }
</style>
